import React, { FC, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu as AntdMenu } from 'antd';
import { ApiOutlined } from '@ant-design/icons';

import useABSToolConfigs from '@lib/customHooks/useABSToolConfigs';
import { useAppContext } from '@lib/providers/app';
import { useAuth } from '@lib/providers/auth';
import { appRoutes, RouteItem } from '@lib/routes/routes';
import { authorized } from '@lib/utils/authorize';

const useMenu = () => {
  const appCtx = useAppContext();
  const { userRoles } = useAuth();

  const routes = appCtx?.routes ?? appRoutes;

  return routes.reduce<RouteItem[]>(function extractMenu(all, item) {
    if (item.hideInMenu) {
      return all;
    }

    if (item.children) {
      const children = item.children.reduce(extractMenu, []);
      if (children.length) {
        all.push({
          ...item,
          children: item.children.reduce(extractMenu, []),
        });
      }
    } else if (item.path && userRoles && (!item.role || authorized(userRoles, item.role))) {
      all.push(item);
    }

    return all;
  }, []);
};

const getMenuMap = (menu: RouteItem[]) =>
  menu.reduce<Record<string, string>>(function extractMenu(all, item) {
    if (item.children) {
      item.children.reduce(extractMenu, all);
    } else {
      all[item.path ?? ''] = item.name;
    }

    return all;
  }, {});

const Menu: FC = () => {
  const location = useLocation();
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const menu = useMenu();
  const { token } = useAuth();
  const { listABSConfigs } = useABSToolConfigs();
  const menuMap = getMenuMap(menu);

  useEffect(() => {
    setSelectedKeys([menuMap[location.pathname]].filter(Boolean));
  }, [location]);

  return (
    <AntdMenu mode="inline" selectedKeys={selectedKeys}>
      {menu.map((item) =>
        item.children ? (
          <AntdMenu.SubMenu key={item.name} title={item.title} icon={item.icon}>
            {item.children.map((child) => (
              <AntdMenu.Item key={child.name} icon={child.icon}>
                {child.path && <Link to={child.path}>{child.title}</Link>}
              </AntdMenu.Item>
            ))}
          </AntdMenu.SubMenu>
        ) : (
          <AntdMenu.Item key={item.name} icon={item.icon}>
            {item.path && <Link to={item.path}>{item.title}</Link>}
          </AntdMenu.Item>
        ),
      )}
      {listABSConfigs.length > 0 && (
        <AntdMenu.SubMenu key="abs-tool" icon={<ApiOutlined />} title="A/B Test">
          {listABSConfigs.map((absConfig) => (
            <AntdMenu.Item key={absConfig?.name}>
              <a
                href={`${absConfig?.absToolHomepageUrl}/login/?accessToken=${token}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {absConfig?.name ?? ''}
              </a>
            </AntdMenu.Item>
          ))}
        </AntdMenu.SubMenu>
      )}
    </AntdMenu>
  );
};

export default Menu;
